import {selectSearchText} from 'components/customer/Product/store/productSlice';
import {TitleAndBreadcrumbs} from 'components/customer/Product/TitleAndBreadcrums';
import {useCategoriesAndItems} from 'hooks/Product/Product';
import React, {useCallback, useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {Categories} from 'shared/components/Product/Categories';
import {ItemType} from 'shared/components/Product/Item';
import {useAppSelector} from 'store/customer';
import styled from 'styled-components';

const ProductSelection = () => {
    const {loader, categories, items} = useCategoriesAndItems();
    const search = useAppSelector(selectSearchText);
    const [selectedProduct, setSelectedProduct] = useState<number>();
    const [searchParams, setSearchParams] = useSearchParams();

    const handleOptionChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSelectedProduct(Number(event.target.value));
            setSearchParams((prevParams) => {
                prevParams.set('cabinetId', event.target.value);
                return prevParams;
            });
        },
        [setSearchParams]
    );

    useEffect(() => {
        const cabinetId = searchParams.get('cabinetId');
        if (!selectedProduct && cabinetId) {
            // preloading a mapped product
            setSelectedProduct(Number(cabinetId));
        } else if (selectedProduct && !cabinetId) {
            // when rechoosing from another category remove the selection
            setSelectedProduct(undefined);
        }
    }, [selectedProduct, searchParams]);

    return (
        <div style={{height: 'calc(100vh - 350px)', overflow: 'auto'}}>
            <TitleAndBreadcrumbs
                categories={categories}
                itemsCount={items ? items.length : 0}
            />
            {search ? null : (
                <Categories
                    isProduct={true}
                    categories={
                        searchParams?.get('cabinetId') ? [] : categories
                    }
                    loader={loader}
                />
            )}
            <RadioGrid>
                {items?.map((item: ItemType) => (
                    <RadioWrapper key={item.id}>
                        <input
                            type="radio"
                            id={`option-${item.id}`}
                            name="customRadioGroup"
                            onChange={handleOptionChange}
                            value={item.id}
                            checked={selectedProduct === item.id}
                            className="radio-input"
                        />
                        <label
                            htmlFor={`option-${item.id}`}
                            className="radio-card">
                            <div className="card-image">
                                <img
                                    src={item.imageUrl}
                                    alt={item.changed_name || item.name}
                                />
                            </div>
                            <div className="card-text">
                                <p>{item.changed_name || item.name}</p>
                            </div>
                        </label>
                    </RadioWrapper>
                ))}
            </RadioGrid>
        </div>
    );
};

const RadioGrid = styled.div`
    margin-top: 15px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(149px, 1fr));
    gap: 15px;
    align-items: start;
    width: 100%;
`;

const RadioWrapper = styled.div`
    position: relative;

    .radio-input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
    }

    .radio-card {
        position: relative;
        display: flex;
        flex-direction: column;
        width: 100%;
        aspect-ratio: 1 / 1;
        background: white;
        border-radius: 12px;
        cursor: pointer;
        overflow: hidden;
        box-sizing: border-box;
        padding: 8px;
    }

    .radio-card::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 12px;
        pointer-events: none;
        z-index: 10;
        box-shadow: inset 0 0 0 0px transparent;

        display: flex;
        align-items: center;
        justify-content: center;

        color: white;
        font-weight: 800;
        font-size: 1.2rem;
        text-transform: uppercase;
    }

    .radio-card:hover::after {
        box-shadow: inset 0 0 0 3px ${({theme}) => theme.colors.secondary.main};
        background-color: rgba(var(--secondary_colour), 0.6);
    }

    .radio-input:checked + .radio-card::after {
        box-shadow: inset 0 0 0 3px ${({theme}) => theme.colors.secondary.main};
        background-color: rgba(var(--secondary_colour), 0.6);
        content: 'Selected';
    }

    .card-image {
        flex: 1;
        min-height: 0;
        width: 100%;
    }

    .card-image img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }

    .card-text {
        flex: 0 0 auto;
        text-align: center;
        font-size: 14px;
        font-family: sans-serif;
        color: #333;
        background: white;
    }
`;

export default ProductSelection;
